import {
  Checkbox,
  Divider,
  TextField,
  Button as UIButton,
} from "@mui/material";
import clsx from "clsx";
import { useEffect, useState } from "react";
import { createUseStyles } from "react-jss";
import { theme } from "$utils/theme/bataillog";

import { Button, Popin, RadioGrp, Text } from "$components";
import useEDBFormulaire from "$hooks/useEDBFormulaire";
import useJStyles from "$hooks/useJStyles";
import { DispositionType } from "$types";
import { emailRegexp } from "$utils/utils";

type EDBPopinProps = {
  edb: any;
  resetEdb: () => void;
  indexEdb: number;
};

function EdbPopin({ edb, resetEdb, indexEdb }: EDBPopinProps) {
  const classes = useStyles();
  const { jclasses } = useJStyles();
  const [openPopin, setOpenPopin] = useState(false);

  useEffect(() => {
    if (edb !== null) {
      setOpenPopin(true);
    } else {
      setOpenPopin(false);
    }
  }, [edb]);

  const {
    handleChangeSTep3Form,
    disposition,
    setDisposition,
    showData,
    dispositionType,
    contactRecovery,
    setDispositionType,
    setContactRecovery,
    setNotes,
    updateEdb,
    setEntrepotName,
    errorForm,
  } = useEDBFormulaire(edb);

  const togglePopin = (open: boolean) => {
    setOpenPopin(open);
    resetEdb();
  };

  const validateInstructions = () => {
    const isUpdated = updateEdb(() => {}, indexEdb);
    if (isUpdated) {
      togglePopin(false);
    }
  };

  return (
    <Popin
      containerClassName={classes.ProductPopin}
      customCloseAction={() => {
        if (resetEdb) resetEdb();
      }}
      openPopin={openPopin}
      toggleDrawer={togglePopin}
    >
      <Text type="h2">Donner les instructions de mise à disposition</Text>
      <div
        className={`${classes.contentStep3} ${
          showData.contact ? "show" : "hidden"
        }`}
      >
        <div className={classes.contentStep3Title}>
          <Text className={classes.subtitle}>Lieu de mise à disposition</Text>
        </div>
        <RadioGrp
          className={classes.contentStep3Content}
          defaultValue={dispositionType}
          radios={[
            {
              label: "Chantier",
              value: DispositionType.ON_DOCKYARD,
            },
            {
              label: "Entrepôt le plus proche",
              value: DispositionType.NEAREST_WAREHOUSE,
            },
            {
              label: "Entrepôt sous-traitant",
              value: DispositionType.SUBCONTRACTED_WAREHOUSE,
            },
          ]}
          onChange={(value) => setDispositionType(value)}
          name="dispositionType"
          withDivider
        />
        <Divider className={classes.divider} />
      </div>

      <div
        className={`${classes.contentStep3} ${
          showData.contact ? "show" : "hidden"
        }`}
      >
        <div className={classes.contentStep3Title}>
          <Text className={classes.subtitle}>
            Contact de récupération du matériel
          </Text>
        </div>
        <div className={clsx(classes.contentStep3Content, classes.flex)}>
          <TextField
            className={clsx(classes.textField, classes.textFieldSpacerRight)}
            label="Nom"
            variant="outlined"
            placeholder="Tapez le nom"
            value={contactRecovery.name.value?.toUpperCase()}
            onChange={(e: any) =>
              handleChangeSTep3Form(
                "name",
                e.target.value,
                contactRecovery,
                setContactRecovery
              )
            }
          />
          <TextField
            className={clsx(classes.textField, classes.textFieldSpacerRight)}
            label="Prénom"
            variant="outlined"
            placeholder="Tapez le prénom"
            value={contactRecovery.surname.value}
            onChange={(e: any) =>
              handleChangeSTep3Form(
                "surname",
                e.target.value,
                contactRecovery,
                setContactRecovery
              )
            }
          />
          <TextField
            className={clsx(classes.textField, classes.textFieldSpacerRight)}
            label="E-mail"
            variant="outlined"
            placeholder="Tapez l'e-mail"
            FormHelperTextProps={{
              className: classes.errorText,
            }}
            value={contactRecovery.email.value}
            helperText={contactRecovery.email.error}
            onChange={(e: any) =>
              handleChangeSTep3Form(
                "email",
                e.target.value,
                contactRecovery,
                setContactRecovery,
                false
              )
            }
            onBlur={(e: any) =>
              handleChangeSTep3Form(
                "email",
                e.target.value,
                contactRecovery,
                setContactRecovery,
                false,
                emailRegexp.toString(),
                "Veuillez saisir une adresse e-mail valide"
              )
            }
          />
          <TextField
            className={classes.textField}
            label="Téléphone"
            variant="outlined"
            placeholder="Tapez le téléphone"
            FormHelperTextProps={{
              className: classes.errorText,
            }}
            value={contactRecovery.phone.value}
            helperText={contactRecovery.phone.error}
            onChange={(e: any) =>
              handleChangeSTep3Form(
                "phone",
                e.target.value,
                contactRecovery,
                setContactRecovery,
                false,
                "^([0-9]){1,10}$"
              )
            }
            onBlur={(e: any) =>
              handleChangeSTep3Form(
                "phone",
                e.target.value,
                contactRecovery,
                setContactRecovery,
                false,
                "([0-9]){10}$",
                "Veuillez saisir un numéro de téléphone valide"
              )
            }
          />
        </div>
        <Divider className={classes.divider} />
      </div>

      <div
        className={`${classes.contentStep3} ${
          showData.dateDisposition ? "show" : "hidden"
        }`}
      >
        <div className={classes.contentStep3Title}>
          <Text className={classes.subtitle}>
            Date de mise à disposition souhaitée
          </Text>
        </div>
        <div
          className={clsx(classes.contentStep3Content)}
          style={{
            flex: 1,
            display: "flex",
          }}
        >
          <TextField
            style={{
              justifyContent: "center",
              maxWidth: 240,
              paddingRight: 20,
            }}
            className={classes.textField}
            type="date"
            variant="outlined"
            value={disposition.dateDisposition.value}
            FormHelperTextProps={{
              className: classes.errorText,
            }}
            helperText={disposition.dateDisposition.error}
            onChange={(e: any) =>
              handleChangeSTep3Form(
                "dateDisposition",
                e.target.value,
                disposition,
                setDisposition,
                disposition.dateDisposition.isRequired,
                "",
                "Veuillez entrer une date valide"
              )
            }
          />
          <div
            className={clsx(classes.contentStep3Content)}
            style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
            }}
          >
            <div
              style={{ cursor: "pointer" }}
              onClick={(e: any) => {
                handleChangeSTep3Form(
                  "dispositionDateIsExact",
                  disposition.dispositionDateIsExact.value === false
                    ? true
                    : !e.target.value,
                  disposition,
                  setDisposition,
                  disposition.dispositionDateIsExact.isRequired
                );
              }}
            >
              <Checkbox
                checked={
                  disposition.dispositionDateIsExact.value === undefined ||
                  disposition.dispositionDateIsExact.value
                }
                color="primary"
                inputProps={{ "aria-label": "secondary checkbox" }}
                defaultChecked
              />
              <Text>Date exacte</Text>
            </div>
            <div
              style={{ cursor: "pointer" }}
              onClick={(e: any) => {
                handleChangeSTep3Form(
                  "dispositionDateIsExact",
                  disposition.dispositionDateIsExact.value
                    ? false
                    : !e.target.value,
                  disposition,
                  setDisposition,
                  disposition.dispositionDateIsExact.isRequired
                );
              }}
            >
              <Checkbox
                checked={disposition.dispositionDateIsExact.value === false}
                color="primary"
                inputProps={{ "aria-label": "secondary checkbox" }}
              />
              <Text>Au plus tard le</Text>
            </div>
          </div>
        </div>
        <Divider className={classes.divider} />
      </div>

      <div
        className={`${classes.contentStep3} ${
          showData.nomEntrepot ? "show" : "hidden"
        }`}
      >
        <div className={classes.contentStep3Title}>
          <Text className={classes.subtitle}>Nom de l&apos;entrepôt</Text>
        </div>
        <div className={clsx(classes.contentStep3Content, classes.flex)}>
          <TextField
            className={classes.textField}
            variant="outlined"
            label="Nom de l'entrepôt"
            placeholder="Tapez le nom de l'entrepôt"
            onChange={(e: any) => setEntrepotName(e.target.value)}
          />
        </div>
        <Divider className={classes.divider} />
      </div>

      <div
        className={`${classes.contentStep3} ${
          showData.adressDisposition ? "show" : "hidden"
        }`}
      >
        <div className={classes.contentStep3Title}>
          <Text className={classes.subtitle}>
            Adresse de mise à disposition souhaitée
          </Text>
        </div>
        <div className={clsx(classes.contentStep3Content)}>
          <div className={clsx(classes.row, classes.flex)}>
            <TextField
              className={classes.textField}
              label="Adresse"
              variant="outlined"
              placeholder="Tapez l'adresse"
              FormHelperTextProps={{
                className: classes.errorText,
              }}
              value={disposition.adress.value}
              helperText={disposition.adress.error}
              disabled={disposition.adress.isDisabled}
              onChange={(e: any) =>
                handleChangeSTep3Form(
                  "adress",
                  e.target.value,
                  disposition,
                  setDisposition,
                  disposition.adress.isRequired
                )
              }
            />
          </div>
          <div className={clsx(classes.row, classes.flex)}>
            <TextField
              className={classes.textField}
              label="Complément d'adresse"
              placeholder="Tapez le complément d'adresse"
              variant="outlined"
              FormHelperTextProps={{
                className: classes.errorText,
              }}
              value={disposition.complementAdress.value}
              helperText={disposition.complementAdress.error}
              disabled={disposition.complementAdress.isDisabled}
              onChange={(e: any) =>
                handleChangeSTep3Form(
                  "complementAdress",
                  e.target.value,
                  disposition,
                  setDisposition,
                  disposition.complementAdress.isRequired
                )
              }
            />
          </div>
          <div className={clsx(classes.row, classes.flex)}>
            <div className={clsx(classes.flex)}>
              <TextField
                className={classes.postalCode}
                label="Code postal"
                placeholder="Tapez le code postal"
                variant="outlined"
                disabled={disposition.postalCode.isDisabled}
                value={disposition.postalCode.value}
                helperText={disposition.postalCode.error}
                FormHelperTextProps={{
                  className: classes.errorText,
                }}
                onChange={(e: any) =>
                  handleChangeSTep3Form(
                    "postalCode",
                    e.target.value,
                    disposition,
                    setDisposition,
                    disposition.postalCode.isRequired
                  )
                }
              />
              <TextField
                className={clsx(classes.textFieldSpacer, classes.textField)}
                label="Ville"
                placeholder="Tapez la ville"
                variant="outlined"
                FormHelperTextProps={{
                  className: classes.errorText,
                }}
                value={disposition.city.value}
                helperText={disposition.city.error}
                disabled={disposition.city.isDisabled}
                onChange={(e: any) =>
                  handleChangeSTep3Form(
                    "city",
                    e.target.value,
                    disposition,
                    setDisposition,
                    disposition.city.isRequired
                  )
                }
              />
              <TextField
                className={classes.textField}
                label="Pays"
                placeholder="Tapez le pays"
                variant="outlined"
                FormHelperTextProps={{
                  className: classes.errorText,
                }}
                value={disposition.country.value}
                helperText={disposition.country.error}
                disabled={disposition.country.isDisabled}
                onChange={(e: any) =>
                  handleChangeSTep3Form(
                    "country",
                    e.target.value,
                    disposition,
                    setDisposition,
                    disposition.country.isRequired
                  )
                }
              />
            </div>
          </div>
        </div>
        <Divider className={classes.divider} />
      </div>

      <div
        className={`${classes.contentStep3} ${
          showData.note ? "show" : "hidden"
        }`}
      >
        <div className={classes.contentStep3Title}>
          <Text className={classes.subtitle}>Notes, contraintes</Text>
        </div>
        <div className={clsx(classes.contentStep3Content, classes.flex)}>
          <TextField
            className={classes.textField}
            placeholder="Tapez vos notes, contraintes..."
            variant="outlined"
            multiline
            rows={4}
            inputProps={{ maxLength: 300 }}
            onChange={(e: any) => setNotes(e.target.value)}
          />
        </div>
      </div>
      <Text className={clsx(classes.errorText, jclasses.jDialogContent)}>
        {errorForm}
      </Text>
      <div className={classes.navigationStep}>
        <UIButton
          variant="outlined"
          style={{ textTransform: "none" }}
          onClick={() => togglePopin(false)}
        >
          Annuler
        </UIButton>
        <Button onClick={() => validateInstructions()}>Valider</Button>
      </div>
    </Popin>
  );
}

export default EdbPopin;

const useStyles = createUseStyles(() => ({
  ProductPopin: {
    minWidth: "959px !important",
    maxWidth: "959px !important",
    "& > div": {
      position: "relative",
    },
  },
  contentStep3Title: {
    marginBottom: 15,
  },
  subtitle: {
    fontWeight: "bold",
  },
  contentStep3Content: {
    "& .MuiInputBase-root.Mui-disabled": {
      background: theme.color.gray2,
    },
  },
  divider: {
    margin: "15px 0 !important",
    display: "block",
  },
  contentStep3: {
    "&.hidden": {
      display: "none",
    },
    "&.show": {
      display: "block",
    },
  },
  textField: {
    flex: 1,
    background: "#fff",
  },
  textFieldSpacer: {
    margin: "0 10px !important",
  },
  textFieldSpacerRight: {
    marginRight: "10px !important",
  },
  errorText: {
    color: `${theme.color.red} !important`,
  },
  flex: {
    display: "flex",
    flex: 1,
  },
  row: {
    marginBottom: 12,
  },
  postalCode: {
    flex: 0.5,
  },
  navigationStep: {
    marginTop: 18,
    marginBottom: 18,
    display: "flex",
    justifyContent: "space-between",
  },
}));
